/**
 * DAT myClaim SOAP Service
 * Handles contract creation, retrieval and calculation data
 */

import { getBackendUrl } from '../utils/apiUrl';

const BACKEND_URL = getBackendUrl();

/**
 * Contract Types Configuration
 */
export const CONTRACT_TYPES = {
  glass: {
    label: 'Glasberechnung',
    contractType: 'glass',
    subView: 'glass',
    invoiceRatesId: '906856',
  },
  vro_calculation: {
    label: 'Schadenkalkulation',
    contractType: 'vro_calculation',
    subView: 'vro_calculation',
    invoiceRatesId: null,
  },
  bundle_valuation: {
    label: 'Wertermittlung',
    contractType: 'bundle_valuation',
    subView: 'bundle_valuation',
    invoiceRatesId: null,
  },
};

/**
 * Create or Update a DAT Contract via Backend
 * @param {Object} dossier - Contract data
 * @param {string} contractType - Type of contract (glass, vro_calculation, bundle_valuation)
 * @param {string} networkType - Network type (default: DAT)
 * @param {string|number} invoiceRatesId - Invoice rates ID
 * @returns {Promise<string>} The claim ID
 */
export async function createOrUpdateContract(dossier, contractType = 'glass', networkType = 'DAT', invoiceRatesId = null) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${BACKEND_URL}/api/dat/contract/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      dossier,
      contractType,
      networkType,
      invoiceRatesId
    })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Fehler beim Erstellen des Contracts');
  }

  const data = await response.json();
  return data.claimId;
}

/**
 * Get Contract details from DAT
 * @param {string} contractId - The claim/contract ID
 * @returns {Promise<Object>} Full contract data with calculation
 */
export async function getContract(contractId) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${BACKEND_URL}/api/dat/contract/${contractId}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Fehler beim Abrufen des Contracts');
  }

  return await response.json();
}

/**
 * Get available invoice rates
 * @param {string} countryCode - ISO country code (default: DE)
 * @returns {Promise<Array>} List of invoice rates
 */
export async function listInvoiceRates(countryCode = 'DE') {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${BACKEND_URL}/api/dat/invoice-rates?country=${countryCode}`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Fehler beim Abrufen der Verrechnungssätze');
  }

  return await response.json();
}

/**
 * Generate PDF from calculation data
 * @param {string} contractId - The claim/contract ID
 * @param {string} caseId - The local case ID
 * @returns {Promise<Object>} PDF URL and status
 */
export async function generateCalculationPdf(contractId, caseId) {
  const token = localStorage.getItem('token');
  
  const response = await fetch(`${BACKEND_URL}/api/dat/contract/${contractId}/pdf`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ caseId })
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Fehler beim Erstellen der PDF');
  }

  return await response.json();
}

/**
 * Map case data to DAT dossier format
 * @param {Object} caseData - The local case data
 * @param {string} caseNumber - The case number/name
 * @returns {Object} Dossier in DAT format
 */
export function mapCaseToDossier(caseData, caseNumber) {
  // Split customer name into first/last name
  const nameParts = (caseData.customer_name || '').split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || nameParts[0] || '';

  // Parse address
  const addressParts = (caseData.customer_address || '').match(/^(.+?)\s+(\d+.*)$/);
  const street = addressParts ? addressParts[1] : caseData.customer_address || '';
  const streetNumber = addressParts ? addressParts[2] : '';

  // Parse PLZ and city
  const plzOrtParts = (caseData.customer_plz_ort || '').match(/^(\d{5})\s+(.+)$/);
  const zipCode = plzOrtParts ? plzOrtParts[1] : '';
  const city = plzOrtParts ? plzOrtParts[2] : caseData.customer_plz_ort || '';

  return {
    name: caseNumber || `Fall-${Date.now()}`,
    country: 'DE',
    language: 'de',
    currency: 'EUR',
    owner: {
      title: caseData.customer_anrede === 'Frau' ? 'titleMs' : 'titleMr',
      firstName: firstName,
      lastName: lastName,
      street: street,
      streetNumber: streetNumber,
      zipCode: zipCode,
      city: city,
      phone: caseData.customer_phone || '',
      email: caseData.customer_email || '',
    },
    vehicle: {
      vin: caseData.vin || '',
      licensePlate: caseData.license_plate || '',
      mileage: caseData.mileage || '',
    }
  };
}

/**
 * Format currency for display
 * @param {number|string} value - The value to format
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value) {
  if (!value) return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(num);
}
